import { useCallback, useEffect, useRef } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { campaignsEndpoints } from '@/api/endpoints';
import { selectAccountSlug } from '@/redux/session/selectors';
import { CampaignType } from '@/shared/types/campaigns';
import { resolveAvailableName } from '@/util/checkNames';

import { createCampaignMutationOptions } from '../api';
import { clearDraftCampaign, updateDraftCampaign } from '../redux/actions';

/**
 * Creates a campaign via the API and navigates to its setup step. Shared by the
 * campaigns page and the command palette so the create flow stays identical.
 * Isolates the create mutation from the heavier `useCampaigns` list query.
 */
export const useCreateCampaign = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const slug = useSelector(selectAccountSlug);

  const { mutateAsync: createCampaignAsync } = useMutation(
    createCampaignMutationOptions(queryClient)
  );

  // Tracks the account on screen now; the flow below keeps the one it started in.
  const currentSlugRef = useRef(slug);
  useEffect(() => {
    currentSlugRef.current = slug;
  }, [slug]);

  const createCampaign = useCallback(
    async (type: CampaignType, name?: string) => {
      if (!slug) {
        toast.error(t('campaigns.create_error'));
        return;
      }

      // Pin the account for the whole flow: a switch between the name check and the create would
      // check names in A and write into B, slipping past the server's UNIQUE (account_id, name).
      const accountSlug = slug;
      const baseName = name?.trim() || t('campaigns.new_campaign');

      // Name resolution needs its own toast; the mutation onError only covers the create call.
      let resolvedName: string;
      try {
        resolvedName = await resolveAvailableName(
          campaignsEndpoints.checkName(baseName),
          baseName,
          { accountSlug }
        );
      } catch {
        toast.error(t('campaigns.create_error'));
        return;
      }

      try {
        const newCampaign = await createCampaignAsync({
          campaign: { name: resolvedName, type },
          accountSlug,
        });
        // Seed the draft only after the campaign exists, so a failed create leaves none behind.
        dispatch(clearDraftCampaign());
        dispatch(updateDraftCampaign({ type }));
        // Don't pull the user back out of an account they switched to mid-create.
        if (currentSlugRef.current === accountSlug) {
          navigate(`/${accountSlug}/campaigns/${newCampaign.campaign_id}/setup`);
        }
      } catch {
        // Error toast is handled by the mutation's onError
      }
    },
    [dispatch, createCampaignAsync, navigate, slug, t]
  );

  return { createCampaign };
};
